import { LogIn, PenTool, FileText, Layers, Zap, Clock } from 'lucide-react';

interface MaintenanceScreenProps {
  theme?: 'dark' | 'light';
  maintenanceMessage?: string;
  onOpenLogin: () => void;
}

export default function MaintenanceScreen({
  theme = 'light',
  maintenanceMessage,
  onOpenLogin,
}: MaintenanceScreenProps) {
  const displayMessage =
    maintenanceMessage?.trim() ||
    "We're currently improving VECTRIA to give you a better experience. Please check back shortly.";

  const isLight = theme === 'light';

  const features = [
    {
      icon: <PenTool size={26} color="var(--palette-0072ff)" strokeWidth={2.1} />,
      title: 'Image to Vector',
      desc: 'Clean & editable results',
    },
    {
      icon: <FileText size={26} color="var(--palette-0072ff)" strokeWidth={2.1} />,
      title: 'AI Metadata Generator',
      desc: 'Titles, keywords & more',
    },
    {
      icon: <Layers size={26} color="var(--palette-0072ff)" strokeWidth={2.1} />,
      title: 'Built for Creators',
      desc: 'From personal projects to microstock',
    },
    {
      icon: <Zap size={26} color="var(--palette-0072ff)" strokeWidth={2.1} />,
      title: 'Your AI, Your Workflow',
      desc: 'Bring your own API key',
    },
  ];

  const dividerColor = isLight ? 'var(--inline-rgba-226-232-240-0-9)' : 'var(--alpha-rgba-255-255-255-0-08)';

  return (
    <div
      className={`maintenance-page-shell theme-${theme}`}
      style={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        backgroundImage: isLight
          ? "linear-gradient(var(--inline-rgba-255-255-255-0-88), var(--alpha-rgba-255-255-255-0-94)), url('/BG-landingpage-dark.webp')"
          : "linear-gradient(var(--inline-rgba-5-8-17-0-45), var(--inline-rgba-5-8-17-0-72)), url('/BG-landingpage-dark.webp')",
        backgroundPosition: 'center center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundColor: isLight ? 'var(--color-white)' : 'var(--palette-050811)',
        color: isLight ? 'var(--palette-0f172a)' : 'var(--md-sys-color-on-surface)',
        fontFamily: "'Plus Jakarta Sans', var(--font-body, system-ui, -apple-system, sans-serif)",
        padding: 'clamp(16px, 2.5vh, 28px) clamp(20px, 4vw, 48px) clamp(20px, 3vh, 32px)',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      {/* Top Navbar */}
      <header
        style={{
          width: '100%',
          maxWidth: '1240px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingBottom: 'clamp(8px, 1.5vh, 16px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img
            src="/emblem.webp"
            alt="Vectria Emblem"
            width="32"
            height="32"
            style={{
              height: '32px',
              width: 'auto',
              objectFit: 'contain',
            }}
            onError={(e) => {
              const target = e.currentTarget;
              if (!target.src.endsWith('/favicon.webp')) {
                target.src = '/favicon.webp';
              }
            }}
          />
          <span
            style={{
              fontSize: '18px',
              fontWeight: 800,
              letterSpacing: '0.08em',
              color: isLight ? 'var(--palette-0f172a)' : 'var(--color-white)',
            }}
          >
            VECTRIA
          </span>
        </div>

        {/* Single Non-Redundant Sign In Button */}
        <button
          type="button"
          onClick={onOpenLogin}
          aria-label="Sign In"
          style={{
            borderRadius: '9999px',
            padding: '8px 20px',
            fontSize: '13px',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            background: isLight ? 'var(--inline-rgba-0-114-255-0-06)' : 'var(--alpha-rgba-255-255-255-0-08)',
            color: isLight ? 'var(--palette-0072ff)' : 'var(--palette-60a5fa)',
            border: isLight ? '1px solid var(--inline-rgba-0-114-255-0-18)' : '1px solid var(--alpha-rgba-255-255-255-0-16)',
            cursor: 'pointer',
            transition: 'all 0.18s ease',
            backdropFilter: 'blur(8px)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isLight ? 'var(--inline-rgba-0-114-255-0-12)' : 'var(--inline-rgba-255-255-255-0-14)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = isLight ? 'var(--inline-rgba-0-114-255-0-06)' : 'var(--alpha-rgba-255-255-255-0-08)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <LogIn size={14} />
          <span>Sign In</span>
        </button>
      </header>

      {/* Main Hero Section: Impactful, Balanced 2 Columns */}
      <main
        style={{
          width: '100%',
          maxWidth: '1240px',
          margin: 'auto auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))',
          alignItems: 'center',
          gap: 'clamp(20px, 3.5vw, 48px)',
          padding: 'clamp(10px, 2vh, 24px) 0',
        }}
      >
        {/* Left Column: Bold Typography without awkward empty space */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', maxWidth: '560px', width: '100%' }}>
          <span
            style={{
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: 0,
              color: isLight ? 'var(--color-disabled-ink)' : 'var(--md-sys-color-on-surface-variant)',
              marginBottom: '12px',
            }}
          >
            We’ll be back soon
          </span>

          <h1
            style={{
              fontSize: 'clamp(46px, 5.2vw, 72px)',
              fontWeight: 900,
              lineHeight: 1.05,
              letterSpacing: '-0.035em',
              margin: '0 0 18px',
              color: isLight ? 'var(--palette-0f172a)' : 'var(--color-white)',
            }}
          >
            Under <br />
            <span
              style={{
                color: isLight ? 'var(--palette-0072ff)' : 'var(--color-chart-current)',
                display: 'inline-block',
              }}
            >
              Maintenance
            </span>
          </h1>

          <p
            style={{
              fontSize: 'clamp(15.5px, 1.5vw, 17.5px)',
              lineHeight: 1.6,
              color: isLight ? 'var(--palette-475569)' : 'var(--md-sys-color-on-surface-variant)',
              margin: 0,
              maxWidth: '520px',
            }}
          >
            {displayMessage}
          </p>
        </div>

        {/* Right Column: Prominently sized Hero Illustration */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <img
            src="/maintenance.webp"
            alt="Vectria Under Maintenance"
            width="620"
            height="460"
            decoding="async"
            style={{
              width: '100%',
              maxWidth: '620px',
              maxHeight: '460px',
              height: 'auto',
              objectFit: 'contain',
              filter: isLight
                ? 'drop-shadow(0 20px 40px var(--inline-rgba-0-114-255-0-12))'
                : 'drop-shadow(0 20px 40px var(--alpha-rgba-0-0-0-0-6)) drop-shadow(0 0 25px var(--inline-rgba-0-114-255-0-22))',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
            onError={(e) => {
              const target = e.currentTarget;
              if (target.src.endsWith('.webp')) {
                target.src = '/maintenance.png';
              }
            }}
          />
        </div>
      </main>

      {/* Bottom Features & Status Banner */}
      <footer
        style={{
          width: '100%',
          maxWidth: '1240px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'clamp(16px, 2.5vh, 24px)',
          paddingTop: 'clamp(12px, 2vh, 20px)',
        }}
      >
        {/* Features Row: 4 Items with elegant vertical dividers */}
        <div
          style={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            paddingTop: 'clamp(12px, 2vh, 18px)',
            borderTop: isLight ? '1px solid var(--inline-rgba-226-232-240-0-9)' : '1px solid var(--alpha-rgba-255-255-255-0-08)',
          }}
        >
          {features.map((feat, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                padding: '8px 16px',
                borderRight: idx < features.length - 1 ? `1px solid ${dividerColor}` : 'none',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  height: '34px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '10px',
                }}
              >
                {feat.icon}
              </div>
              <div
                style={{
                  fontSize: '13.5px',
                  fontWeight: 700,
                  color: isLight ? 'var(--palette-0f172a)' : 'var(--md-sys-color-on-surface)',
                  marginBottom: '3px',
                }}
              >
                {feat.title}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: isLight ? 'var(--color-disabled-ink)' : 'var(--md-sys-color-on-surface-variant)',
                  lineHeight: 1.4,
                }}
              >
                {feat.desc}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Status Pill matching mockup */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 22px',
            borderRadius: '9999px',
            background: isLight ? 'var(--palette-f1f6fe)' : 'var(--inline-rgba-15-23-42-0-8)',
            border: isLight ? '1px solid var(--palette-dce8fa)' : '1px solid var(--inline-rgba-56-189-248-0-22)',
            boxShadow: isLight
              ? '0 2px 12px var(--inline-rgba-0-114-255-0-04)'
              : '0 4px 20px var(--alpha-rgba-0-0-0-0-3)',
          }}
        >
          <div
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: isLight ? 'var(--palette-e0edff)' : 'var(--inline-rgba-56-189-248-0-18)',
              color: 'var(--palette-0072ff)',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <Clock size={16} strokeWidth={2.2} />
          </div>
          <div style={{ textAlign: 'left' }}>
            <div
              style={{
                fontSize: '12px',
                fontWeight: 700,
                color: isLight ? 'var(--palette-0f172a)' : 'var(--md-sys-color-on-surface)',
                lineHeight: 1.3,
              }}
            >
              We’ll be back soon!
            </div>
            <div
              style={{
                fontSize: '11px',
                color: isLight ? 'var(--color-disabled-ink)' : 'var(--md-sys-color-on-surface-variant)',
                lineHeight: 1.3,
              }}
            >
              Thank you for your patience and support.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

